import { ToastOptions } from "@ionic/core";
import { translate } from "./translate";
import { showGlobalToast } from "./toast";
import {
  ActivityKey,
  HealthIntegrationKey,
  User,
  WorkoutLog,
} from "@sworkit/api";
import { addWorkoutLog, updateWorkoutLog } from "./firestore-workout-logs";
import SworkitMobile, { NativeWorkoutLog } from "./sworkit-mobile/sw-mobile";
import { jsVersionWithBuildNumber } from "./app";
import { arrayUnion } from "firebase/firestore";
import healthIntegration from "../stencil-store/health.store";

export interface HealthLogIos {
  dataType: "activity";
  value: ActivityKey;
  //https://github.com/dariosalvi78/cordova-plugin-health/blob/master/activities_map.md
  startDate: Date;
  endDate: Date;
  duration?: number;
  calories: number;
}
export const healthIntegrationKeys: HealthIntegrationKey[] = [
  "apple-health",
  "google-fit",
  "health-connect",
];

export const isHealthIntegrationConnected = (
  integration: HealthIntegrationKey,
) => {
  return healthIntegration.readEnabledIntegrations?.includes(integration) ||
    healthIntegration.writeEnabledIntegrations?.includes(integration);
};

export interface HealthLog extends WorkoutLog {
  minutes: string;
  endTime: string;
  integration?: HealthIntegrationKey;
}
export function isNativeHealthIntegrationKey(
  searchKey: string | HealthIntegrationKey,
) {
  const setOfKeys = new Set(healthIntegrationKeys);
  return setOfKeys.has(searchKey as HealthIntegrationKey);
}

export async function isHealthAuthorized(integration: HealthIntegrationKey) {
  try {
    const { available } = await SworkitMobile.isHealthAvailable({
      integration,
    });
    if (!available) {
      console.error("not authorized to sync to health");
      return Promise.reject("Error syncing workout log");
    }
  } catch (err) {
    console.error("authorization to health failed", err);
    return Promise.reject("Error syncing workout log");
  }
}

export async function syncToHealth(log: WorkoutLog) {
  for (const integration of healthIntegration.writeEnabledIntegrations || []) {
    try {
      await storeHealthLog(log, integration);
      await updateSavedToHealthField(log, integration);
      return showHealthSyncedToast();
    } catch (err) {
      console.error("error syncing to healthkit", err);
      return Promise.reject("Error syncing workout log");
    }
  }
}

export const hydrateLog = (
  nativeLog: NativeWorkoutLog,
): Partial<WorkoutLog> => {
  return {
    ...nativeLog,
    appVersion: jsVersionWithBuildNumber,
    name: translate(nativeLog.activityKey),
  };
};

export async function syncFromHealth(nativeLog: NativeWorkoutLog, user: User) {
  try {
    const hydratedLog = hydrateLog(nativeLog) as WorkoutLog;
    return addWorkoutLog(hydratedLog, user);
  } catch (err) {
    console.error("error syncing from health", err);
    return Promise.reject("Error syncing workout log");
  }
}

async function storeHealthLog(
  log: WorkoutLog,
  integration: HealthIntegrationKey,
) {
  if (!SworkitMobile.healthStore) return Promise.reject();
  // technically, just need the extra converted string types for Android :
  // todo:update, we can probably use numbers in kotlin
  const healthLog: HealthLog = {
    ...log,
    endTime: log?.timestampMillis?.toString(10),
    minutes: log?.minutesCompleted?.toString(10),
    integration,
  };
  // console.log("ADDING TO HEALTH", healthLog)
  return SworkitMobile.healthStore(healthLog);
}

async function showHealthSyncedToast() {
  const options: ToastOptions = {
    message: translate("HEALTH_SYNCED"),
    position: "top",
    duration: 3000,
    color: "dark",
  };
  showGlobalToast(options);
}
// todohealth
// export async function showSyncedFromHealthToast(num) {
//   const options: ToastOptions = {
//     message: translate('IMPORTED_WORKOUTS_FROM_HEALTH', {
//       smart_count: num,
//       health_app_name: translate(healthAppNameTranslateKey),
//     }),
//     position: 'top',
//     duration: 3000,
//     color: 'dark',
//   };
//   showGlobalToast(options);
// }

function updateSavedToHealthField(
  log: WorkoutLog,
  integration: HealthIntegrationKey,
) {
  if (!log.id) return;
  if (log.savedToHealthIntegrations?.includes(integration)) return;
  return updateWorkoutLog(log.id, {
    savedToHealthIntegrations: arrayUnion(integration),
  });
}
